import React, {
    useCallback,
    useEffect,
    useEffectEvent,
    useMemo,
    useState,
} from "react";
import { hashKey, useQueryClient } from "@tanstack/react-query";
import {
    Task,
    TaskStatus,
    TERMINATING_TASK_STATUSES,
} from "@common/types/task";
import {
    CounterpartyDirectoryView,
    CounterpartyDirectoryViewSchema,
} from "@common/types/views/counterpartyDirectoryView";
import { View } from "@common/types/views/views";
import { counterpartiesQueryKeys } from "@api/counterparty.api";
import { useUpdateTask } from "@api/task.api";
import { useUser } from "@hooks/useUser";
import { FiltersChangeFn } from "@components/pageHeader/filters/lib";
import { DirectoryFilters } from "@components/settings/Directory/lib";
import {
    defaultForm1099CloseTemplateView,
    parseForm1099CloseTemplateViewConfig,
    upgradeLegacyForm1099Columns,
} from "@components/settings/OrganizationCloseTemplates/closeTemplateTaskViewConfig";
import { ensureForm1099Eligible1099ReviewColumn } from "@components/settings/OrganizationCloseTemplates/form1099ReviewColumn";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { StickyTaskMembershipPending } from "../shared/StickyTaskMembershipPending";
import { useStickyRefreshOnMount } from "../shared/useStickyRefreshOnMount";
import { CounterpartiesTaskTable } from "../Counterparties/CounterpartiesTaskTable";
import { toDirectoryFilters } from "../Counterparties/counterpartiesTaskView";
import counterpartiesStyles from "../Counterparties/Counterparties.module.scss";
import { FORM_1099_FILTER_TABS } from "./form1099FilterTabs";
import {
    buildForm1099ReviewQueryFilters,
    Form1099ReviewFilterMode,
    getForm1099ImplicitFilterLabels,
} from "./form1099ReviewFilters";
import { Form1099ReviewModeToggle } from "./Form1099ReviewModeToggle";

const DEFAULT_REVIEW_MODE: Form1099ReviewFilterMode = "suggested";

interface Props {
    selectedTask: Task;
}

function parseTaskView(task: Task): CounterpartyDirectoryView {
    const parsed = CounterpartyDirectoryViewSchema.safeParse(task.view);
    if (parsed.success) {
        return ensureForm1099Eligible1099ReviewColumn(
            upgradeLegacyForm1099Columns(parsed.data),
        );
    }
    if (!task.view) {
        return parseForm1099CloseTemplateViewConfig(null);
    }
    return parseForm1099CloseTemplateViewConfig({
        type: task.view.type,
        columns: task.view.columns,
        filters: task.view.filters,
        options: task.view.options ?? undefined,
    });
}

export const Form1099TaskBody: React.FC<Props> = ({ selectedTask }) => {
    const user = useUser();
    const queryClient = useQueryClient();
    const updateTask = useUpdateTask(selectedTask);
    const defaultView = useMemo(() => defaultForm1099CloseTemplateView(), []);

    const [view, setView] = useState<CounterpartyDirectoryView>(() =>
        parseTaskView(selectedTask),
    );
    // Sticky tasks keep period/entity on live filters and matching criteria in
    // originalFilters. Prefer originalFilters for the directory date scope so
    // locked creator filters stay authoritative when present.
    const originalFilters = selectedTask.view?.originalFilters ?? undefined;
    const [scope, setScope] = useState<DirectoryFilters>(() =>
        toDirectoryFilters(originalFilters ?? view.filters),
    );
    const [activeReviewMode, setActiveReviewMode] =
        useState<Form1099ReviewFilterMode>(DEFAULT_REVIEW_MODE);

    const taskViewKey = useMemo(
        () => hashKey([selectedTask.view]),
        [selectedTask.view],
    );

    const resyncFromTask = useEffectEvent(() => {
        const nextView = parseTaskView(selectedTask);
        setView(nextView);
        setScope(
            toDirectoryFilters(
                selectedTask.view?.originalFilters ?? nextView.filters,
            ),
        );
        setActiveReviewMode(DEFAULT_REVIEW_MODE);
    });
    // Keyed on the view content, not its object identity: the task detail
    // query returns a structurally identical view on every refetch, and
    // re-syncing then would discard local state.
    useEffect(() => {
        resyncFromTask();
    }, [selectedTask.id, taskViewKey]);

    const invalidateCounterpartiesList = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: counterpartiesQueryKeys.query(selectedTask.workspaceId),
        });
    }, [queryClient, selectedTask.workspaceId]);

    const { isMembershipPending, resolvedMembership } = useStickyRefreshOnMount(
        {
            selectedTask,
            onMembershipAdded: invalidateCounterpartiesList,
        },
    );
    const stickyCounterpartiesIds =
        resolvedMembership?.counterpartiesIds ??
        selectedTask.view?.filters?.counterpartiesIds;

    const handleViewChange = useCallback((updated: View) => {
        setView((previous) => {
            const parsed = CounterpartyDirectoryViewSchema.safeParse({
                ...previous,
                columns: updated.columns,
                filters: { ...previous.filters, ...updated.filters },
                options: updated.options ?? previous.options,
            });
            return parsed.success
                ? ensureForm1099Eligible1099ReviewColumn(parsed.data)
                : previous;
        });
    }, []);

    const handleFiltersChange = useCallback<FiltersChangeFn>((payload) => {
        setView((previous) => ({
            ...previous,
            filters: { ...previous.filters, ...payload },
        }));
    }, []);

    const reviewQueryFilters = useMemo(
        () =>
            buildForm1099ReviewQueryFilters({
                mode: activeReviewMode,
                userId: user.id,
            }),
        [activeReviewMode, user.id],
    );

    const implicitFilterLabels = useMemo(
        () => getForm1099ImplicitFilterLabels(activeReviewMode),
        [activeReviewMode],
    );

    const handleReviewModeChange = useCallback(
        (mode: Form1099ReviewFilterMode | null) => {
            if (mode) {
                setActiveReviewMode(mode);
            }
        },
        [],
    );

    const canMarkDone = !TERMINATING_TASK_STATUSES.includes(
        selectedTask.status,
    );

    const handleMarkDone = useCallback(() => {
        updateTask.mutate({ status: TaskStatus.DONE });
    }, [updateTask]);

    const handleShowAllVendors = useCallback(() => {
        setActiveReviewMode("all");
    }, []);

    if (isMembershipPending) {
        return <StickyTaskMembershipPending />;
    }

    return (
        <div className={counterpartiesStyles.root}>
            <CounterpartiesTaskTable
                key={selectedTask.id}
                queryScopeKey={selectedTask.id}
                view={view}
                onViewChange={handleViewChange}
                onFiltersChange={handleFiltersChange}
                scope={scope}
                onScopeChange={setScope}
                availableFilters={originalFilters ? [] : FORM_1099_FILTER_TABS}
                defaultView={defaultView}
                lockScope
                hideEntityFilter
                collapseLockedFilters
                implicitFilterLabels={implicitFilterLabels}
                singleEntitySelection
                originalFilters={originalFilters}
                stickyCounterpartiesIds={stickyCounterpartiesIds}
                extraQueryFilters={reviewQueryFilters}
                reviewMode={
                    activeReviewMode === "reviewed" ? "unreview" : "review"
                }
                headerActions={
                    <Form1099ReviewModeToggle
                        value={activeReviewMode}
                        onValueChange={handleReviewModeChange}
                    />
                }
                className={counterpartiesStyles.filters}
                tableClassName={counterpartiesStyles.table}
                emptyView={() =>
                    activeReviewMode === "suggested" ? (
                        <EmptyState
                            header="No suggested vendors to review"
                            ctaText="Mark as done"
                            action={canMarkDone ? handleMarkDone : undefined}
                            secondaryCtaText="Show all vendors"
                            secondaryAction={handleShowAllVendors}
                        />
                    ) : (
                        <EmptyState
                            header={
                                activeReviewMode === "reviewed"
                                    ? "No reviewed counterparties"
                                    : "No counterparties matching the task definition"
                            }
                        />
                    )
                }
            />
        </div>
    );
};
